import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { TabNav, Badge, Pagination, Card, StarRating, Spinner } from '../../../components/common';
import ReviewEditModal from '../../../components/service/ReviewEditModal';
import { userService, reviewService } from '../../../api/services';
import { makeServiceLabels, getBadgeServiceType } from '../../../constants/serviceLabels';
import { myPageTabs } from '../../../constants/uiConstants';
import { useDeepLTranslated } from '../../../hooks/useDeepLTranslated';
import { formatKstDateTime } from '../../../utils/dateUtils';

export default function MyReviews() {
  const { t } = useTranslation();
  const serviceLabels = makeServiceLabels(t);
  const navigate = useNavigate();
  const [reviews, setReviews] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [editingReview, setEditingReview] = useState(null);

  // 후기 본문을 현재 UI 언어로 자동 번역 (한국어 작성 후기도 다국어 페이지에서 번역 노출)
  const reviewContentBundle = useMemo(() => reviews.map((r) => r.content || ''), [reviews]);
  const tReviewContents = useDeepLTranslated(reviewContentBundle);

  useEffect(() => {
    if (!localStorage.getItem('accessToken')) { navigate('/login', { replace: true }); return; }
    setLoading(true);
    userService.getMyReviews({ page: currentPage, size: 10 })
      .then((data) => {
        const items = Array.isArray(data) ? data : (data.items || []);
        setReviews(items);
        setTotalPages(data.pages || Math.ceil(items.length / 10) || 1);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [currentPage]);

  const handleTabChange = (tabId) => {
    if (tabId === 'account') {
      navigate('/mypage/account');
    } else if (tabId === 'reservation') {
      navigate('/reservation');
    }
  };

  const handleSaveReview = (id, { rating, text }) => {
    reviewService.update(id, { rating, content: text })
      .then(() => {
        setReviews((prev) => prev.map((r) => r.id === id ? { ...r, rating, content: text } : r));
        setEditingReview(null);
      })
      .catch((err) => alert(err.message));
  };

  const handleDeleteReview = (id) => {
    reviewService.delete(id)
      .then(() => {
        setReviews((prev) => prev.filter((r) => r.id !== id));
        setEditingReview(null);
      })
      .catch((err) => alert(err.message));
  };

  return (
    <div className="bg-mono-50 min-h-screen">
      {/* TabNav - fullwidth */}
      <TabNav tabs={myPageTabs} activeTab="reviews" onTabChange={handleTabChange} />

      <div className="max-w-content mx-auto px-2.5 sm:px-5 py-8 sm:py-12">

        {/* Section Heading */}
        <h2 className="text-xl font-bold text-mono-900 mt-8 mb-6">{t('mypage.my_reviews')}</h2>

        {/* Review Cards */}
        <div className="space-y-4">
          {loading && <div className="flex justify-center py-8"><Spinner /></div>}
          {!loading && reviews.map((review, i) => (
            <Card
              key={review.id}
              variant="padded"
              interactive
              onClick={() => setEditingReview(review)}
            >
              {/* Top Row: Badge + Star Rating */}
              <div className="flex items-center gap-3 mb-3">
                <Badge variant="service" serviceType={getBadgeServiceType(review.service_type)}>{(review.service_type && serviceLabels[review.service_type]) || review.service || review.service_label || t('nav.private_transfer')}</Badge>
                <div className="flex items-center gap-1.5">
                  <StarRating rating={review.rating} showEmpty={false} />
                  <span className="text-sm font-bold text-mono-900">{review.rating?.toFixed(1)}</span>
                </div>
              </div>

              {/* Review Body */}
              <p className="text-sm text-mono-900 leading-relaxed mb-3">
                {tReviewContents[i] || review.content}
              </p>

              {/* Created Date */}
              <p className="text-2sm text-mono-500">
                {t('mypage.join_date')} {formatKstDateTime(review.created_at) || '-'}
              </p>
            </Card>
          ))}

          {!loading && reviews.length === 0 && (
            <div className="py-16 text-center">
              <p className="text-sm text-mono-400">{t('mypage.no_reviews_written')}</p>
            </div>
          )}
        </div>

        {/* Pagination */}
        {reviews.length > 0 && (
          <Pagination
            current={currentPage}
            total={totalPages}
            onChange={setCurrentPage}
            className="mt-8"
          />
        )}
      </div>

      {/* Review Edit Modal */}
      {editingReview && (
        <ReviewEditModal
          review={editingReview}
          onClose={() => setEditingReview(null)}
          onSave={handleSaveReview}
          onDelete={handleDeleteReview}
        />
      )}
    </div>
  );
}
